/**
 * The application's wired client: real fetch, the session store for the bearer, and the
 * single-flight refresher whose outcomes update the session.
 */
import { publicEndpoints } from './endpoints/public'
import { createApiClient } from './client'
import { actionsEndpoints } from './endpoints/actions'
import { authEndpoints } from './endpoints/auth'
import { providersEndpoints, resourcesEndpoints, servicesEndpoints } from './endpoints/catalog'
import { customersEndpoints } from './endpoints/customers'
import { conversationsEndpoints } from './endpoints/conversations'
import { messagesEndpoints, templatesEndpoints } from './endpoints/messaging'
import {
  auditLogEndpoints,
  billingEndpoints,
  brandingAdminEndpoints,
  integrationsEndpoints,
} from './endpoints/admin'
import {
  platformBillingEndpoints,
  platformOrganizationsEndpoints,
  resellersEndpoints,
} from './endpoints/platform'
import { reportsEndpoints } from './endpoints/reports'
import { workflowRulesEndpoints } from './endpoints/workflow-rules'
import { recoveryEndpoints } from './endpoints/recovery'
import {
  appointmentsEndpoints,
  availabilityEndpoints,
  waitlistEndpoints,
} from './endpoints/scheduling'
import { capabilitiesEndpoints } from './endpoints/capabilities'
import {
  brandingEndpoints,
  locationsEndpoints,
  notificationsEndpoints,
  organizationsEndpoints,
  usersEndpoints,
} from './endpoints/tenancy'
import { createRefresher, type RefreshOutcome } from '@/auth/refresh'
import { getAccessToken, useSessionStore } from '@/auth/session-store'
import { env } from '@/config/env'

function applyRefreshOutcome(outcome: RefreshOutcome): void {
  const store = useSessionStore.getState()
  switch (outcome.kind) {
    case 'ok':
      store.setSession(outcome.session)
      return
    case 'replay':
      store.clear('replay')
      return
    case 'unauthenticated':
      store.clear(outcome.code === 'TOKEN_EXPIRED' ? 'expired' : 'invalid')
      return
    case 'error':
      // Transient. Keep whatever session exists; the next call will try again.
      return
  }
}

/**
 * Same-origin fetch. `env.apiBase` is a relative path; browsers resolve it against the page, but
 * resolving explicitly keeps the behaviour identical under jsdom/Node (tests) and is a no-op in
 * the browser.
 */
const sameOriginFetch: typeof fetch = (input, init) =>
  globalThis.fetch(
    typeof input === 'string' ? new URL(input, globalThis.location.href) : input,
    init,
  )

export const refresher = createRefresher({
  apiBase: env.apiBase,
  fetch: sameOriginFetch,
  onOutcome: applyRefreshOutcome,
})

export const api = createApiClient({
  apiBase: env.apiBase,
  fetch: sameOriginFetch,
  getAccessToken,
  refresh: refresher.refresh,
})

export const endpoints = {
  auth: authEndpoints(api),
  capabilities: capabilitiesEndpoints(api),
  branding: brandingEndpoints(api),
  locations: locationsEndpoints(api),
  organizations: organizationsEndpoints(api),
  notifications: notificationsEndpoints(api),
  users: usersEndpoints(api),
  actions: actionsEndpoints(api),
  services: servicesEndpoints(api),
  resources: resourcesEndpoints(api),
  providers: providersEndpoints(api),
  customers: customersEndpoints(api),
  appointments: appointmentsEndpoints(api),
  availability: availabilityEndpoints(api),
  waitlist: waitlistEndpoints(api),
  recovery: recoveryEndpoints(api),
  conversations: conversationsEndpoints(api),
  templates: templatesEndpoints(api),
  messages: messagesEndpoints(api),
  workflowRules: workflowRulesEndpoints(api),
  reports: reportsEndpoints(api),
  brandingAdmin: brandingAdminEndpoints(api),
  integrations: integrationsEndpoints(api),
  billing: billingEndpoints(api),
  auditLog: auditLogEndpoints(api),
  resellers: resellersEndpoints(api),
  platformOrganizations: platformOrganizationsEndpoints(api),
  platformBilling: platformBillingEndpoints(api),
  // RecoupSlots P11 — the public tenant API behind /o/:slug.
  public: publicEndpoints(api),
}

export { ApiError, isApiError } from './errors'
export type { ApiErrorKind } from './errors'
export type { Page, Unwrapped } from './envelope'
export type * from './types'
